import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import api from "@/lib/api";

const fmt = (n) => (n || 0).toLocaleString();

// Polls /inboxes/sync-status — fast while work is running, slow when idle.
export function useSyncStatus() {
  const [inboxes, setInboxes] = useState([]);
  useEffect(() => {
    let alive = true;
    let timer;
    const tick = async () => {
      let busy = false;
      try {
        const { data } = await api.get("/inboxes/sync-status");
        if (!alive) return;
        const rows = data.inboxes || [];
        setInboxes(rows);
        busy = rows.some((i) => (i.sync_in_progress && i.sync_total > 0) || i.backfill_status === "running");
      } catch (e) { void e; }
      if (alive) timer = setTimeout(tick, busy ? 3000 : 15000);
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, []);
  return inboxes;
}

function Bar({ pct, testId }) {
  return (
    <div className="h-1 w-full bg-zinc-100 rounded-full overflow-hidden" data-testid={testId}>
      <div
        className="h-full bg-[#0033CC] rounded-full transition-[width] duration-700 ease-out"
        style={{ width: `${Math.max(3, Math.min(100, pct))}%` }}
      />
    </div>
  );
}

export function SyncProgress({ inboxes }) {
  const syncing = (inboxes || []).filter((i) => i.sync_in_progress && i.sync_total > 0);
  const backfilling = (inboxes || []).filter((i) => i.backfill_status === "running");
  if (!syncing.length && !backfilling.length) return null;

  const total = syncing.reduce((s, i) => s + (i.sync_total || 0), 0);
  const done = syncing.reduce((s, i) => s + (i.sync_done || 0), 0);
  const seen = backfilling.reduce((s, i) => s + (i.backfill_seen || 0), 0);
  const est = backfilling.reduce((s, i) => s + (i.backfill_total_estimate || 0), 0);
  const imported = backfilling.reduce((s, i) => s + (i.backfill_fetched || 0), 0);

  return (
    <div className="px-4 py-2 border-b border-zinc-100 bg-zinc-50/70 space-y-2" data-testid="sync-progress">
      {syncing.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-zinc-700">
            <Loader2 className="w-3 h-3 animate-spin text-[#0033CC]" strokeWidth={2} />
            <span data-testid="sync-progress-text">
              Downloading new mail · <b className="tabular-nums">{fmt(done)}</b> of {fmt(total)}
            </span>
          </div>
          <Bar pct={total ? (done / total) * 100 : 0} testId="sync-progress-bar" />
        </div>
      )}
      {backfilling.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-zinc-500">
            <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2} />
            <span data-testid="backfill-progress-text">
              Importing older mail in the background · {fmt(imported)} added
              {est ? ` · ${Math.min(99, Math.floor((seen / est) * 100))}% scanned` : ""}
            </span>
          </div>
          {est > 0 && <Bar pct={(seen / est) * 100} testId="backfill-progress-bar" />}
        </div>
      )}
    </div>
  );
}
